import { SearchX } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import type { CandidateFilters } from "@/types/candidates"

import { ActiveFilterChips } from "./active-filter-chips"

/**
 * OKS-17 — "Filters returned 0 rows" panel (UX Spec §States: No-results).
 *
 * Distinct from the empty state. Discriminator: `is_empty: false &&
 * candidates.length === 0`. Filter rail and search remain interactive
 * — the panel sits ABOVE where the table would be, not replacing it.
 *
 * Locked testids:
 *   - `candidate-list-no-results` — panel root
 *   - `candidate-list-no-results-clear` — clear-filters affordance
 */
interface CandidateNoResultsPanelProps {
  filters: CandidateFilters
  onClearAll: () => void
  onRemoveFilter: (key: keyof CandidateFilters, value?: string) => void
}

export function CandidateNoResultsPanel({
  filters,
  onClearAll,
  onRemoveFilter,
}: CandidateNoResultsPanelProps) {
  const { t } = useTranslation()
  return (
    <div
      data-testid="candidate-list-no-results"
      role="status"
      className="bg-muted/30 flex flex-col items-start gap-4 rounded-md border p-6"
    >
      <div className="flex items-start gap-3">
        <SearchX
          className="text-muted-foreground mt-1 size-5"
          aria-hidden="true"
        />
        <div className="flex flex-col gap-1">
          <h2 className="font-semibold">
            {t("candidates.list.no_results.title")}
          </h2>
          <p className="text-muted-foreground text-sm">
            {t("candidates.list.no_results.description")}
          </p>
        </div>
      </div>
      <ActiveFilterChips filters={filters} onRemove={onRemoveFilter} />
      <Button
        type="button"
        variant="ghost"
        data-testid="candidate-list-no-results-clear"
        onClick={onClearAll}
      >
        {t("candidates.list.no_results.clear")}
      </Button>
    </div>
  )
}
